import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import {
  FocusedJourneyLayout, MaskContainer, MaskContext, MaskHeadline,
  MaskInput, MaskTextarea, PrimaryAction, SecondaryAction, MaskBack, MaskClose, CharCounter,
} from '@/components/mask/MaskSystem';
import { composeInvite } from '@/lib/momentInvite';

/* MOMENTS 1.0 — Initiator: Moment erstellen (I01 FÜR WEN → I02 ANLASS → I03 ZEIT → I04 EINLADEN) */

const CUSTOM = '__custom';

export default function MomentCreateV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [occasion, setOccasion] = useState('');
  const [customOccasion, setCustomOccasion] = useState('');
  const [deliverAt, setDeliverAt] = useState('');
  const [deadline, setDeadline] = useState('');
  const [manualRelease, setManualRelease] = useState(false);
  const [notify, setNotify] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [selfText, setSelfText] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inviteText, setInviteText] = useState('');
  const [invitePreview, setInvitePreview] = useState(false);
  const [inviteSaved, setInviteSaved] = useState(false);

  if (!user) { navigate('/auth?redirect=/momente/neu'); return null; }
  const firstName = name.trim().split(' ')[0];
  const occasions = t('v2.moments.occasions', { returnObjects: true });
  const occasionLabel = occasion === CUSTOM ? customOccasion.trim() : occasion;

  const create = async () => {
    if (creating) return;
    setCreating(true); setError(null);
    try {
      const res = await axios.post(`${API_V2}/moments`, {
        recipientName: name.trim(),
        occasion: occasionLabel,
        deliverAt: manualRelease || !deliverAt ? null : new Date(deliverAt).toISOString(),
        deadline: deadline ? new Date(deadline).toISOString() : null,
        notify,
        notifyEmail: notify && notifyEmail,
      }, { withCredentials: true });
      setInviteLink(`${window.location.origin}/moment/einladung/${res.data.inviteToken}`);
      window.__lastMomentId = res.data.id;
      setInviteText(t('v2.moments.inviteDefault', { name: name.trim().split(' ')[0] }));
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || t('v2.common.error'));
    } finally { setCreating(false); }
  };

  const composedText = () => composeInvite(t, firstName, inviteText, inviteLink);

  const saveInviteText = async () => {
    const def = t('v2.moments.inviteDefault', { name: firstName });
    const custom = inviteText.trim() === def.trim() ? null : inviteText.trim();
    try {
      await axios.put(`${API_V2}/moments/${window.__lastMomentId}/invite-text`, { inviteText: custom }, { withCredentials: true });
      setInviteSaved(true); setTimeout(() => setInviteSaved(false), 2000);
    } catch (_e) { /* still shareable client-side */ }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(composedText()); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch (_e) { /* blocked */ }
  };

  return (
    <FocusedJourneyLayout>
      <MaskClose onClick={() => navigate('/momente')} data-testid="moment-close-btn" />
      {step > 0 && step < 3 && <MaskBack onClick={() => setStep(step - 1)} data-testid="moment-back-btn" />}

      {step === 0 && (
        <MaskContainer maskKey="i01" bgWord={t('v2.bgWords.fuerWen')} bgPosition="top" bgShift="12%" data-testid="moment-i01">
          <MaskContext>{t('v2.moments.context0')}</MaskContext>
          <MaskHeadline>{t('v2.moments.headline0')}</MaskHeadline>
          <MaskInput
            autoFocus value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) setStep(1); }}
            placeholder={t('v2.give.namePlaceholder')} data-testid="moment-name-input"
          />
          <div className="mt-10">
            <PrimaryAction onClick={() => setStep(1)} disabled={!name.trim()} data-testid="moment-i01-next">{t('v2.common.continue')}</PrimaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 1 && (
        <MaskContainer maskKey="i02" bgWord={t('v2.bgWords.anlass')} bgPosition="bottom" bgShift="-12%" data-testid="moment-i02">
          <MaskContext>{t('v2.common.for', { name: name.trim() })}</MaskContext>
          <MaskHeadline>{t('v2.moments.headline1')}</MaskHeadline>
          <div className="space-y-1">
            {occasions.map((o) => (
              <button key={o} type="button" onClick={() => { setOccasion(o); setStep(2); }} data-testid={`moment-occasion-${o}`}
                className="mask-person-row block w-full text-left text-lg text-[var(--text)]/80 py-3 px-3 rounded-lg">
                {o}
              </button>
            ))}
            <div className="pt-3">
              <MaskInput
                value={customOccasion}
                onChange={(e) => { setCustomOccasion(e.target.value.slice(0, 60)); setOccasion(CUSTOM); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && customOccasion.trim()) setStep(2); }}
                placeholder={t('v2.moments.customOccasion')} data-testid="moment-occasion-custom"
              />
              {customOccasion.trim() && (
                <div className="mt-4"><SecondaryAction onClick={() => setStep(2)} data-testid="moment-occasion-custom-next">{t('v2.common.continue')}</SecondaryAction></div>
              )}
            </div>
          </div>
        </MaskContainer>
      )}

      {step === 2 && (
        <MaskContainer maskKey="i03" bgWord={t('v2.bgWords.moment')} bgPosition="top" bgShift="-8%" data-testid="moment-i03">
          <MaskContext>{occasionLabel}</MaskContext>
          <MaskHeadline>{t('v2.moments.headline2', { name: firstName })}</MaskHeadline>
          {!manualRelease && (
            <div className="mb-8">
              <label className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-2">{t('v2.moments.handover')}</label>
              <input type="datetime-local" value={deliverAt} onChange={(e) => setDeliverAt(e.target.value)}
                data-testid="moment-deliver-input" className="mask-input text-lg text-[var(--text)] bg-transparent" />
            </div>
          )}
          <button type="button" onClick={() => setManualRelease(!manualRelease)} data-testid="moment-manual-toggle"
            className={`block text-sm py-2 mb-8 underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors ${manualRelease ? 'text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
            {manualRelease ? `${t('v2.moments.manualRelease')} ✓` : t('v2.moments.manualRelease')}
          </button>
          <div className="mb-10">
            <label className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-2">{t('v2.moments.deadlineLabel')}</label>
            <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)}
              data-testid="moment-deadline-input" className="mask-input text-lg text-[var(--text)] bg-transparent" />
          </div>
          <div className="mb-8 space-y-3">
            <button type="button" onClick={() => setNotify(!notify)} data-testid="moment-notify-toggle"
              className={`block text-sm py-1 text-left transition-colors ${notify ? 'text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
              {notify ? '☑' : '☐'} {t('v2.moments.notify')}
            </button>
            {notify && (
              <button type="button" onClick={() => setNotifyEmail(!notifyEmail)} data-testid="moment-notify-email-toggle"
                className={`block text-sm py-1 pl-5 text-left transition-colors ${notifyEmail ? 'text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
                {notifyEmail ? '☑' : '☐'} {t('v2.moments.notifyEmail')}
              </button>
            )}
          </div>
          {error && <p className="text-sm text-red-700/80 mb-4" data-testid="moment-create-error">{error}</p>}
          <PrimaryAction onClick={create} disabled={creating || (!manualRelease && !deliverAt)} data-testid="moment-create-btn">
            {creating ? t('v2.common.loading') : t('v2.moments.create')}
          </PrimaryAction>
        </MaskContainer>
      )}

      {step === 5 && (
        <MaskContainer maskKey="self" bgWord={t('v2.bgWords.vonDir')} bgPosition="top" bgShift="12%" data-testid="moment-self-step">
          <MaskContext>{t('v2.moments.selfContext')}</MaskContext>
          <MaskHeadline>{t('v2.moments.selfHeadline', { name: firstName })}</MaskHeadline>
          <MaskTextarea
            value={selfText}
            onChange={(e) => setSelfText(e.target.value.slice(0, 10000))}
            placeholder={t('v2.give.msgPlaceholder')}
            style={{ minHeight: '22vh' }}
            data-testid="moment-self-textarea"
          />
          <CharCounter value={selfText} max={10000} />
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <PrimaryAction
              onClick={async () => {
                if (!selfText.trim()) return;
                try {
                  await axios.post(`${API_V2}/moments/${window.__lastMomentId}/contribute-self`, { message: selfText.trim() }, { withCredentials: true });
                  setStep(3);
                } catch (_e) { setError(t('v2.common.errorShort')); }
              }}
              disabled={!selfText.trim()}
              data-testid="moment-self-send-btn"
            >
              {t('v2.moments.sayNow')}
            </PrimaryAction>
            <SecondaryAction onClick={() => setStep(3)} data-testid="moment-self-later-btn">{t('v2.common.later')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 3 && (
        <MaskContainer maskKey="i04" bgWord={t('v2.bgWords.zusammen')} align="center" bgShift="-8%" data-testid="moment-i04">
          <MaskContext>{t('v2.common.for', { name: name.trim() })}</MaskContext>
          <MaskHeadline>{t('v2.moments.inviteHeadline', { name: firstName })}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md mx-auto">
            {t('v2.moments.inviteText')}
          </p>
          <div className="mask-enter mask-enter-delay-2 text-left max-w-md mx-auto mb-10" data-testid="invite-editor">
            <div className="flex items-baseline justify-between mb-1.5">
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{t('v2.moments.inviteLabel')}</p>
              {inviteSaved && <span className="text-xs text-[var(--muted)]" data-testid="invite-saved">{t('v2.moments.inviteSaved')}</span>}
            </div>
            <p className="text-xs text-[var(--muted)] mb-4">{t('v2.moments.inviteSub')}</p>
            <textarea
              value={inviteText}
              onChange={(e) => setInviteText(e.target.value.slice(0, 1200))}
              onBlur={saveInviteText}
              data-testid="invite-text-input"
              className="mask-textarea w-full text-base text-[var(--text)] min-h-[16vh]"
            />
            <div className="flex items-center gap-5 mt-2">
              <button type="button" onClick={() => { const def = t('v2.moments.inviteDefault', { name: firstName }); setInviteText(def); axios.put(`${API_V2}/moments/${window.__lastMomentId}/invite-text`, { inviteText: null }, { withCredentials: true }).catch(() => {}); }}
                data-testid="invite-reset-btn"
                className="text-xs text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                {t('v2.moments.inviteReset')}
              </button>
              <button type="button" onClick={() => setInvitePreview(!invitePreview)} data-testid="invite-preview-toggle"
                className="text-xs text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                {t('v2.moments.invitePreview')}
              </button>
            </div>
            {invitePreview && (
              <p className="mask-letter text-sm text-[var(--text)]/75 whitespace-pre-line mt-5 pl-4 border-l border-[var(--border)]/50" data-testid="invite-preview">
                {composedText()}
              </p>
            )}
          </div>
          <div className="flex flex-col items-center gap-4">
            <PrimaryAction onClick={copy} data-testid="moment-copy-invite-btn">{copied ? t('v2.common.copied') : t('v2.moments.copyInvite')}</PrimaryAction>
            <div className="mask-enter mask-enter-delay-3 flex gap-6">
              <a href={`https://wa.me/?text=${encodeURIComponent(composedText())}`} target="_blank" rel="noreferrer" className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2" data-testid="moment-share-whatsapp">{t('v2.common.perWhatsApp')}</a>
              <a href={`mailto:?subject=${encodeURIComponent(t('v2.moments.mailSubject', { name: firstName }))}&body=${encodeURIComponent(composedText())}`} className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2" data-testid="moment-share-email">{t('v2.common.perEmail')}</a>
            </div>
            <SecondaryAction onClick={() => navigate(`/momente/${window.__lastMomentId}`)} data-testid="moment-to-manage-btn">{t('v2.moments.toMoment')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}
    </FocusedJourneyLayout>
  );
}
